import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { fileUrl } from '@/lib/api';
import { Gift, Store, ZoomIn, ZoomOut, X, MapPin, Image as ImageIcon } from 'lucide-react';

const META = {
  free: { labelKey: 'list.free', Icon: Gift, color: '#7A9A78' },
  yard_sale: { labelKey: 'list.yardSale', Icon: Store, color: '#D99E4A' },
};
const fmt = (iso) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const SORTS = [
  { key: 'newest', labelKey: 'list.sortNewest' },
  { key: 'nearest', labelKey: 'list.sortNearest' },
  { key: 'ending', labelKey: 'list.sortEnding' },
];

function distanceMiles(a, b) {
  if (!a || !b) return null;
  const R = 3958.8;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat), lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}
const fmtMi = (mi) => mi == null ? null : (mi < 0.1 ? '<0.1 mi' : `${mi.toFixed(mi < 10 ? 1 : 0)} mi`);

export default function ListPanel({ pins = [], center, onSelect, onClose, onZoomIn, onZoomOut }) {
  const { t } = useTranslation();
  const [sort, setSort] = useState('newest');
  const now = Date.now();

  const enriched = useMemo(() => {
    const c = center ? { lat: center.lat, lng: center.lng } : null;
    const arr = pins.map((p) => {
      const dist = c ? distanceMiles(c, { lat: p.lat, lng: p.lng }) : null;
      const endsInMs = new Date(p.ends_at).getTime() - now;
      const isNew = now - new Date(p.created_at).getTime() < 24 * 3600 * 1000;
      const endingSoon = endsInMs > 0 && endsInMs < 24 * 3600 * 1000;
      return { ...p, _dist: dist, _isNew: isNew, _endingSoon: endingSoon, _endsInMs: endsInMs };
    });
    arr.sort((a, b) => {
      if (sort === 'nearest') return (a._dist ?? 1e9) - (b._dist ?? 1e9);
      if (sort === 'ending') return a._endsInMs - b._endsInMs;
      return new Date(b.created_at) - new Date(a.created_at);
    });
    return arr;
  }, [pins, center, sort, now]);

  return (
    <motion.div
      data-testid="list-panel"
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ type: 'spring', damping: 26, stiffness: 280 }}
      className="glass-panel rounded-3xl w-[88vw] sm:w-[400px] max-h-[60vh] flex flex-col overflow-hidden shadow-2xl"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-black/10 shrink-0">
        <div className="font-display font-bold text-brand-ink flex items-center gap-2">
          <MapPin className="w-4 h-4 text-brand" />
          {t('list.pins', { count: pins.length })}
        </div>
        <div className="flex items-center gap-1">
          <button data-testid="list-zoom-in-btn" onClick={onZoomIn} title={t('list.zoomIn')} className="w-8 h-8 rounded-full hover:bg-black/5 flex items-center justify-center"><ZoomIn className="w-4 h-4 text-brand-ink" /></button>
          <button data-testid="list-zoom-out-btn" onClick={onZoomOut} title={t('list.zoomOut')} className="w-8 h-8 rounded-full hover:bg-black/5 flex items-center justify-center"><ZoomOut className="w-4 h-4 text-brand-ink" /></button>
          <button data-testid="list-close-btn" onClick={onClose} title={t('list.closeList')} className="w-8 h-8 rounded-full hover:bg-black/5 flex items-center justify-center"><X className="w-4 h-4 text-brand-ink" /></button>
        </div>
      </div>

      {/* Sort tabs */}
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-black/5 shrink-0">
        {SORTS.map((s) => (
          <button
            key={s.key}
            data-testid={`list-sort-${s.key}`}
            onClick={() => setSort(s.key)}
            className={`rounded-full px-3 py-1 text-xs font-semibold transition ${sort === s.key ? 'bg-brand text-white' : 'bg-black/5 text-brand-slate hover:bg-black/10'}`}
          >
            {t(s.labelKey)}
          </button>
        ))}
      </div>

      <div className="overflow-y-auto">
        {enriched.length === 0 ? (
          <div className="p-8 text-center text-brand-slate text-sm" data-testid="list-empty">
            {t('list.empty')}
          </div>
        ) : enriched.map((p) => {
          const m = META[p.category] || META.free;
          const Icon = m.Icon;
          const photo = p.photos?.[0];
          return (
            <button
              key={p.id}
              data-testid={`list-item-${p.id}`}
              onClick={() => onSelect(p)}
              className="w-full flex items-center gap-3 px-4 py-3 border-b border-black/5 last:border-0 hover:bg-black/5 transition text-left"
            >
              <div className="w-12 h-12 rounded-xl overflow-hidden bg-black/5 flex items-center justify-center shrink-0">
                {photo && photo !== 'default_yard_sale'
                  ? <img src={fileUrl(photo)} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                  : <ImageIcon className="w-5 h-5 text-brand-dim" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-brand-ink truncate">{p.title}</div>
                <div className="text-xs text-brand-slate truncate flex items-center gap-1.5">
                  <Icon className="w-3.5 h-3.5" style={{ color: m.color }} />
                  {t(m.labelKey)} · {fmt(p.starts_at)}–{fmt(p.ends_at)}
                </div>
                <div className="flex flex-wrap items-center gap-1 mt-1">
                  {p._isNew && <span data-testid="tag-new" className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: '#7A9A7822', color: '#5C7A5A' }}>{t('list.new')}</span>}
                  {p._endingSoon && <span data-testid="tag-ending" className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: '#C86F5322', color: '#C86F53' }}>{t('list.endingSoon')}</span>}
                  {p._dist != null && <span data-testid="tag-distance" className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: '#2C554514', color: '#2C5545' }}>{fmtMi(p._dist)}</span>}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </motion.div>
  );
}
